import * as React from 'react'
import { AlertTriangle, CheckCircle2, Info, Radio, Sparkles, X, XCircle } from 'lucide-react'
import { cn } from '@/lib/cn'
import type { Tone } from '@/components/ui/badge'

const DISMISS_MS = 4000
const MAX_VISIBLE = 4

export interface ToastOptions {
  title: string
  description?: string
  tone?: Tone
}

interface ToastRecord extends ToastOptions {
  id: number
}

interface ToastApi {
  toast: (t: ToastOptions) => void
}

const ToastContext = React.createContext<ToastApi | null>(null)

let sequence = 0

const TONE_STYLE: Record<Tone, { ring: string; icon: React.ComponentType<{ className?: string }> }> =
  {
    live: { ring: 'text-live', icon: Radio },
    ok: { ring: 'text-ok', icon: CheckCircle2 },
    info: { ring: 'text-info', icon: Info },
    warn: { ring: 'text-warn', icon: AlertTriangle },
    danger: { ring: 'text-danger', icon: XCircle },
    neutral: { ring: 'text-muted', icon: Info },
    accent: { ring: 'text-accent', icon: Sparkles },
  }

export function ToastProvider(p: { children: React.ReactNode }) {
  const [items, setItems] = React.useState<ToastRecord[]>([])
  const timers = React.useRef(new Map<number, number>())

  const dismiss = React.useCallback((id: number) => {
    const handle = timers.current.get(id)
    if (handle !== undefined) {
      window.clearTimeout(handle)
      timers.current.delete(id)
    }
    // The overflow trim below can drop a toast before its timer fires; bail out
    // rather than churning a new array for a toast that is already gone.
    setItems((prev) => (prev.some((t) => t.id === id) ? prev.filter((t) => t.id !== id) : prev))
  }, [])

  const toast = React.useCallback(
    (t: ToastOptions) => {
      sequence += 1
      const id = sequence
      // Keep only the newest MAX_VISIBLE — an operator does not read a stack.
      setItems((prev) => [...prev, { ...t, id }].slice(-MAX_VISIBLE))
      timers.current.set(
        id,
        window.setTimeout(() => dismiss(id), DISMISS_MS)
      )
    },
    [dismiss]
  )

  React.useEffect(() => {
    const map = timers.current
    return () => {
      for (const handle of map.values()) window.clearTimeout(handle)
      map.clear()
    }
  }, [])

  const api = React.useMemo<ToastApi>(() => ({ toast }), [toast])

  return (
    <ToastContext.Provider value={api}>
      {p.children}

      {/* Corner stack on a laptop; a full-width strip clear of the home
          indicator on a phone, where a 360px-wide card pinned to one corner
          just looks like a mistake.

          FAILURE MODE FIXED: `role="status" aria-live="polite"` used to sit on
          each toast, so the live region entered the DOM in the same commit as
          its text. Assistive technology observes mutations *inside* regions it
          already knows about — a region that appears already-populated is
          frequently not announced at all, which meant "Could not stop the
          session" was silently swallowed. The viewport below is mounted for the
          life of the app, so it is the live region; each toast is now just a
          child insertion into a region the screen reader is already watching.

          `aria-atomic="false"` matters here too: `role="status"` defaults to
          atomic, which would re-read all four visible toasts every time a
          fifth arrived. */}
      <div
        role="status"
        aria-live="polite"
        aria-atomic="false"
        className={cn(
          'pointer-events-none fixed z-[80] flex flex-col gap-2',
          'inset-x-3 bottom-[calc(0.75rem+env(safe-area-inset-bottom))]',
          'sm:inset-x-auto sm:right-5 sm:bottom-5 sm:w-[368px]'
        )}
      >
        {items.map((item) => {
          const tone = TONE_STYLE[item.tone ?? 'neutral']
          const Icon = tone.icon

          return (
            <div
              key={item.id}
              className={cn(
                'pointer-events-auto flex items-start gap-3 rounded-lg border border-line',
                'bg-s2 p-3.5 shadow-e3',
                'transition-[opacity,transform] duration-150 ease-out',
                'starting:translate-y-2 starting:opacity-0'
              )}
            >
              <Icon className={cn('mt-px size-[18px] flex-none', tone.ring)} />

              <div className="min-w-0 flex-1">
                <p className="text-[13.5px] leading-[1.4] font-medium text-fg">{item.title}</p>
                {item.description ? (
                  <p className="mt-1 text-[12.5px] leading-[1.5] break-words text-muted">
                    {item.description}
                  </p>
                ) : null}
              </div>

              <button
                type="button"
                aria-label={`Dismiss notification: ${item.title}`}
                onClick={() => dismiss(item.id)}
                className={cn(
                  '-my-1 -mr-1.5 flex size-8 flex-none items-center justify-center rounded-md text-faint',
                  'transition-colors duration-100 hover:bg-s3 hover:text-fg',
                  'max-sm:-my-2 max-sm:size-11'
                )}
              >
                <X className="size-4" aria-hidden="true" />
              </button>
            </div>
          )
        })}
      </div>
    </ToastContext.Provider>
  )
}

export function useToast(): { toast: (t: ToastOptions) => void } {
  const ctx = React.useContext(ToastContext)
  if (!ctx) throw new Error('useToast must be used within <ToastProvider>')
  return ctx
}
